import { ActionIcon, Slider, Text } from "@mantine/core";
import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import classes from "./AudioPlayer.module.css";

/** The one player that sounds: starting another stops it, as live's players do. */
let stopPlaying: { element: HTMLAudioElement; stop: () => void } | null = null;

/** `element` starts: the one that played before stops (every player on the page calls this). */
export function playsAlone(element: HTMLAudioElement): void {
  if (stopPlaying !== null && stopPlaying.element !== element) stopPlaying.stop();
  stopPlaying = { element, stop: () => element.pause() };
}

/** `element` stopped: no player is playing. */
export function stoppedPlaying(element: HTMLAudioElement): void {
  if (stopPlaying?.element === element) stopPlaying = null;
}

/** Seconds as "mm:ss", the way live's players show a time. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const minutes = Math.floor(whole / 60);
  return `${String(minutes).padStart(2, "0")}:${String(whole % 60).padStart(2, "0")}`;
}

export interface AudioPlayerProps {
  /** The file, at a URL the server signed. */
  url: string;
  /** The file's name, shown and read as the player's name. */
  name: string;
  /** Starts at once, as live's mini player does when a sound is chosen. */
  autoPlay?: boolean;
  /** The length the server knows, shown until the file says its own. */
  durationSeconds?: number | null | undefined;
  /** A link that no longer loads: the caller reads the list again for a fresh one. */
  onError?: () => void;
  /** Live's seek bar; a voice's preview has none. */
  seek?: boolean;
  /** Live's player for a file with problems: it neither plays nor seeks. */
  disabled?: boolean;
  /** Told when it starts or stops: a lightbox's bars move while it plays. */
  onPlayingChange?: ((playing: boolean) => void) | undefined;
}

/**
 * Live's audio player: play and pause, the time over the length, the name, and a seek bar. Only
 * one plays at a time on the page.
 */
export function AudioPlayer({
  url,
  name,
  autoPlay = false,
  durationSeconds,
  onError,
  seek = true,
  disabled = false,
  onPlayingChange,
}: AudioPlayerProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(durationSeconds ?? 0);

  // Leaving the page (or another file) stops this one.
  useEffect(() => {
    const element = audio.current;
    return () => element?.pause();
  }, []);

  const toggle = () => {
    const element = audio.current;
    if (element === null) return;
    // The browser may refuse to play without a gesture; the button is one.
    if (element.paused) void element.play().catch(() => setPlaying(false));
    else element.pause();
  };

  return (
    <div className={classes.player}>
      <ActionIcon
        variant="light"
        size={32}
        radius="xl"
        className={classes.toggle}
        aria-label={playing ? `Pause ${name}` : `Play ${name}`}
        disabled={disabled}
        onClick={toggle}
      >
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </ActionIcon>
      <Text className={classes.time}>
        {clock(time)} / {clock(length)}
      </Text>
      <Text className={classes.name} title={name}>
        {name}
      </Text>
      {seek ? (
        <Slider
          className={classes.seek}
          size="sm"
          min={0}
          max={Math.max(length, 0.01)}
          step={0.01}
          value={Math.min(time, length)}
          label={null}
          aria-label={`Seek in ${name}`}
          disabled={disabled}
          onChange={(seconds) => {
            if (audio.current !== null) audio.current.currentTime = seconds;
            setTime(seconds);
          }}
        />
      ) : null}
      {/* A sound file has no captions to give. */}
      {/* oxlint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audio}
        src={url}
        preload="metadata"
        autoPlay={autoPlay}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const seconds = event.currentTarget.duration;
          if (Number.isFinite(seconds)) setLength(seconds);
        }}
        onEnded={(event) => {
          event.currentTarget.currentTime = 0;
          setTime(0);
        }}
        onPlay={(event) => {
          // Starting this one stops the one that played.
          playsAlone(event.currentTarget);
          setPlaying(true);
          onPlayingChange?.(true);
        }}
        onPause={(event) => {
          stoppedPlaying(event.currentTarget);
          setPlaying(false);
          onPlayingChange?.(false);
        }}
        onError={onError}
      />
    </div>
  );
}
